import {Injectable} from "@angular/core"
import {NodeId, SocketId} from "@node-editor/models"
import {SocketDirection} from "@socket-editor/types"
import {ConnectionsService, ConnectionType} from "@app/socket-editor/services/connections.service"
import {Point} from "@app/node-editor/types/navigation"
import {merge, filter, map} from "rxjs"

export type ConnectionSocket = {nodeId?: NodeId; socketId?: SocketId; position: Point; color?: string; refreshPosition?: () => Promise<Point>}
export type ConnectedSocket = {nodeId: NodeId; socketId: SocketId; position: Point; color?: string; refreshPosition?: () => Promise<Point>}

// connection whose destination is an input socket (the source may or may not be a socket)
export type InputConnection = ConnectionType<ConnectionSocket> & {destination: ConnectedSocket}
// connection whose source is an input socket (the destination may or may not be a socket)
export type OutputConnection = ConnectionType<ConnectionSocket> & {source: ConnectedSocket}

export type SocketConnection = ConnectionType<ConnectionSocket>

@Injectable({
    providedIn: "root",
})
export class SocketConnectionsService extends ConnectionsService<SocketConnection> {
    /*
     * In some cases the socket needs to get deactivated/hidden depending on the settings of the node. In these cases, if there is a connection to this socket,
     * it needs to be removed.
     */
    deactivate = (nodeId: NodeId, socket: {direction: SocketDirection; id: SocketId}) => {
        switch (socket.direction) {
            case "input":
                this.remove(this.inputs(nodeId, socket.id).map((connection) => connection.id))
                break
            case "output":
                this.remove(this.outputs(nodeId, socket.id).map((connection) => connection.id))
        }
    }

    inputs = (nodeId?: NodeId, socketId?: string) => {
        if (!nodeId) {
            return []
        }
        return this.connections.filter(
            (connection): connection is InputConnection =>
                connection.destination?.nodeId === nodeId && (!socketId || socketId === connection.destination?.socketId),
        )
    }

    outputs = (nodeId?: NodeId, socketId?: string) => {
        if (!nodeId) {
            return []
        }
        return this.connections.filter(
            (connection): connection is OutputConnection => connection.source?.nodeId === nodeId && (!socketId || socketId === connection.source?.socketId),
        )
    }

    forNode = (nodeId: string) => {
        return this.connections.filter((connection) => connection.source?.nodeId === nodeId || connection.destination?.nodeId === nodeId)
    }

    forNodes = (nodeIds: string[] | Set<string>) => {
        const connectionIdsSet = new Set([...nodeIds].map((nodeId) => this.forNode(nodeId).map((connection) => connection.id)).flat())
        return [...connectionIdsSet]
    }

    clearNode = (nodeId: string) => {
        const connectionsToNode = this.forNode(nodeId)
        this.remove(connectionsToNode.map((connection) => connection.id))
    }

    forSocket = (nodeId: NodeId, socketId: string, direction: SocketDirection) => {
        return direction === "input" ? this.inputs(nodeId, socketId) : this.outputs(nodeId, socketId)
    }

    clearSocket = (nodeId: NodeId, socketId: string, direction: SocketDirection) => {
        const connectionsToSocket = this.forSocket(nodeId, socketId, direction)
        this.remove(connectionsToSocket.map((connection) => connection.id))
    }

    changesForSocket = (nodeId: NodeId, socketId: string, direction: SocketDirection) => {
        // any one of these says the socket's connections may have changed; waiting for all three means a load
        // that adds connections and deletes none never reports anything at all
        return merge(this.onAdd$, this.onUpdate$, this.onDelete$).pipe(
            filter((connections) => connections.some((connection) => connection.source?.nodeId === nodeId || connection.destination?.nodeId === nodeId)),
            map(() => {
                return this.forSocket(nodeId, socketId, direction)
            }),
        )
    }

    refreshSocketPositions = async (nodeId?: NodeId, socketId?: string) => {
        if (nodeId) {
            const nodeInputs = this.inputs(nodeId, socketId)
            const nodeOutputs = this.outputs(nodeId, socketId)

            const updatedConnections = []
            for (const input of nodeInputs) {
                if (input.destination.refreshPosition) {
                    updatedConnections.push({...input, destination: {...input.destination, position: await input.destination.refreshPosition()}})
                }
            }
            for (const output of nodeOutputs) {
                if (output.source.refreshPosition) {
                    updatedConnections.push({...output, source: {...output.source, position: await output.source.refreshPosition()}})
                }
            }
            this.update(updatedConnections)
        } else {
            const updatedConnections = []
            for (const connection of this.connections) {
                updatedConnections.push({
                    ...connection,
                    destination: connection.destination.refreshPosition
                        ? {...connection.destination, position: await connection.destination.refreshPosition()}
                        : connection.destination,
                    source: connection.source.refreshPosition ? {...connection.source, position: await connection.source.refreshPosition()} : connection.source,
                })
            }
            this.update(updatedConnections)
        }
    }
}
